import { getRetryAfterMs, pickLocalizedApiMessage } from 'pi-kiosk-shared';
import { responseHasHttpStormDetected } from './httpStormDetectedLatch.js';
import i18n from '../../i18n.js';
import {
  looksLikeDeveloperErrorMessage,
  publicMessageForHttpStatus,
  resolvePublicApiFailureMessage,
} from '../errors/publicApiFailureMessage.js';
import {
  isMissingCredentialUnauthorized,
  MISSING_CUSTOMER_SESSION_CREDENTIAL_MESSAGE,
} from '../session/fetchPlatformMe.js';

export const HTTP_STORM_DETECTED_CODE = 'HTTP_STORM_DETECTED' as const;

export interface ResolvedHttpApiError {
  message: string;
  code: string | undefined;
  retryAfterMs?: number;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  return value as Record<string, unknown>;
}

function readNestedErrorFields(errorField: unknown): {
  message: string | undefined;
  code: string | undefined;
  details: Record<string, unknown> | undefined;
} {
  if (typeof errorField === 'string') {
    return { message: errorField, code: undefined, details: undefined };
  }
  if (errorField !== null && typeof errorField === 'object') {
    const nested = errorField as {
      code?: unknown;
      message?: unknown;
      details?: unknown;
    };
    const detailsRecord = asRecord(nested.details);
    return {
      message: typeof nested.message === 'string' ? nested.message : undefined,
      code: typeof nested.code === 'string' ? nested.code : undefined,
      details: detailsRecord ?? undefined,
    };
  }
  return { message: undefined, code: undefined, details: undefined };
}

/** Parse flat or nested `{ success: false, error, code?, details? }` API envelopes. */
export function parseApiErrorEnvelope(body: unknown): {
  message: string | undefined;
  code: string | undefined;
  /** Preserved structured details (incl. entitlementDenials) — never drop. */
  details: Record<string, unknown> | undefined;
} {
  if (body === null || typeof body !== 'object') {
    return { message: undefined, code: undefined, details: undefined };
  }
  const record = body as {
    success?: unknown;
    error?: unknown;
    code?: unknown;
    details?: unknown;
  };
  if (record.success !== false) {
    return { message: undefined, code: undefined, details: undefined };
  }

  const topLevelCode = typeof record.code === 'string' ? record.code : undefined;
  const nested = readNestedErrorFields(record.error);
  const topDetails = asRecord(record.details);
  return {
    message: nested.message,
    code: nested.code ?? topLevelCode,
    details: topDetails ?? nested.details,
  };
}

function resolvePublicHttpFallback(
  status: number,
  fallbackMessage: string | undefined,
): string {
  if (
    fallbackMessage !== undefined &&
    looksLikeDeveloperErrorMessage(fallbackMessage) === false
  ) {
    return fallbackMessage;
  }
  return publicMessageForHttpStatus(status);
}

/**
 * Normalize HTTP error responses for ApiError construction.
 * Tolerates nested storm bodies, flat error strings, and HTTP_STORM_DETECTED header fallback.
 * Messages are customer-safe except the missing-cred protocol sentinel (kept for
 * isMissingCredentialUnauthorized / dying-access; UI must use toCustomerFacingErrorMessage).
 * No ApiClient import — avoids cycles with ApiClient → this module.
 */
export function resolveApiErrorFromHttpResponse(input: {
  response: Response;
  body: unknown;
  fallbackMessage?: string;
}): ResolvedHttpApiError {
  const { response, body } = input;
  const status = response.status;
  const publicFallback = resolvePublicHttpFallback(status, input.fallbackMessage);

  const parsed = parseApiErrorEnvelope(body);
  let code = parsed.code;

  if (status === 429 && code === undefined && responseHasHttpStormDetected(response.headers)) {
    code = HTTP_STORM_DETECTED_CODE;
  }

  const seedRaw =
    parsed.message !== undefined && parsed.message.trim().length > 0
      ? pickLocalizedApiMessage(parsed.message, i18n.resolvedLanguage ?? i18n.language)
      : publicFallback;

  // G6 — keep protocol sentinel on ApiError.message for isMissingCredentialUnauthorized /
  // dying-access. resolvePublicApiFailureMessage never returns it (UI → errors.auth.title).
  const message = isMissingCredentialUnauthorized(code, parsed.message ?? seedRaw)
    ? MISSING_CUSTOMER_SESSION_CREDENTIAL_MESSAGE
    : resolvePublicApiFailureMessage({
        status,
        code,
        rawMessage: seedRaw,
      });

  const resolved: ResolvedHttpApiError = { message, code };
  if (status === 429) {
    resolved.retryAfterMs = getRetryAfterMs({ response, data: body });
  }
  return resolved;
}
